import { useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import { TAGS, TagKey } from './TagManager';

// ═══════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════

interface DataPreviewTableProps {
    data: string[][];
    columnTags: Record<number, TagKey>;
    validationErrors: Map<number, Set<number>>;
    onDrop: (colIndex: number) => void;
    onRemoveTag: (colIndex: number) => void;
    onRemoveColumn: (colIndex: number) => void;
    onRemoveRow: (rowIndex: number) => void;
    onCellEdit: (rowIndex: number, colIndex: number, value: string) => void;
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function DataPreviewTable({
    data,
    columnTags,
    validationErrors,
    onDrop,
    onRemoveTag,
    onRemoveColumn,
    onRemoveRow,
    onCellEdit,
}: DataPreviewTableProps) {
    const [editingCell, setEditingCell] = useState<{ row: number; col: number } | null>(null);
    const [editValue, setEditValue] = useState('');

    const startEditing = (rowIndex: number, colIndex: number, currentValue: string) => {
        setEditingCell({ row: rowIndex, col: colIndex });
        setEditValue(currentValue);
    };

    const saveEdit = () => {
        if (editingCell) {
            onCellEdit(editingCell.row, editingCell.col, editValue);
            setEditingCell(null);
            setEditValue('');
        }
    };

    const cancelEdit = () => {
        setEditingCell(null);
        setEditValue('');
    };

    return (
        <div className="overflow-x-auto border border-gray-200 dark:border-slate-600 rounded-lg">
            <table className="w-full bg-white dark:bg-slate-800">
                <thead>
                    <tr>
                        <th className="bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 p-2 w-12"></th>
                        {data[0].map((_, colIndex) => (
                            <th
                                key={colIndex}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => onDrop(colIndex)}
                                className="bg-blue-50 dark:bg-slate-700 border-2 border-dashed border-blue-300 dark:border-blue-600 p-3 min-w-[150px]"
                            >
                                {columnTags[colIndex] ? (
                                    <div className="flex items-center justify-between gap-2">
                                        <span className={`${TAGS[columnTags[colIndex]].color} text-white px-2 py-1 rounded text-sm font-medium`}>
                                            {TAGS[columnTags[colIndex]].name}
                                        </span>
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => onRemoveTag(colIndex)}
                                                className="bg-orange-500 hover:bg-orange-600 text-white rounded px-2 py-1 text-xs"
                                                title="Remover tag"
                                            >
                                                ↺
                                            </button>
                                            <button
                                                onClick={() => onRemoveColumn(colIndex)}
                                                className="bg-red-500 hover:bg-red-600 text-white rounded px-2 py-1 text-xs"
                                                title="Remover coluna"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-gray-500 dark:text-gray-400 text-sm">Arraste tag aqui</span>
                                        <button
                                            onClick={() => onRemoveColumn(colIndex)}
                                            className="bg-red-500 hover:bg-red-600 text-white rounded px-2 py-1 text-xs"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data
                        .map((row, rowIndex) => ({ row, rowIndex }))
                        .sort((a, b) => {
                            const aHasError = validationErrors.has(a.rowIndex);
                            const bHasError = validationErrors.has(b.rowIndex);
                            if (aHasError && !bHasError) return -1;
                            if (!aHasError && bHasError) return 1;
                            return 0;
                        })
                        .map(({ row, rowIndex }) => {
                            const rowErrors = validationErrors.get(rowIndex);
                            const hasError = rowErrors && rowErrors.size > 0;

                            return (
                                <tr
                                    key={rowIndex}
                                    className={`${hasError ? 'bg-red-100 dark:bg-red-900/30' : rowIndex % 2 === 0 ? 'bg-gray-50 dark:bg-slate-700' : 'bg-white dark:bg-slate-800'}`}
                                >
                                    <td className="border border-gray-300 dark:border-slate-600 p-2 text-center">
                                        <button
                                            onClick={() => onRemoveRow(rowIndex)}
                                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                            title="Remover linha"
                                        >
                                            <FaTrash />
                                        </button>
                                    </td>
                                    {row.map((cell, colIndex) => {
                                        const hasFieldError = rowErrors?.has(colIndex);
                                        const isEditing = editingCell?.row === rowIndex && editingCell?.col === colIndex;

                                        return (
                                            <td
                                                key={colIndex}
                                                className={`border border-gray-300 dark:border-slate-600 p-2 ${hasFieldError
                                                    ? 'bg-red-600 dark:bg-red-700 text-white font-semibold'
                                                    : 'dark:text-gray-200'
                                                } ${isEditing ? '' : 'cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-600'}`}
                                                title={hasFieldError ? 'Campo obrigatório vazio ou inválido' : 'Clique para editar'}
                                                onClick={() => !isEditing && startEditing(rowIndex, colIndex, cell)}
                                            >
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        onBlur={saveEdit}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') saveEdit();
                                                            if (e.key === 'Escape') cancelEdit();
                                                        }}
                                                        autoFocus
                                                        className="w-full px-1 py-0 border-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                                                    />
                                                ) : (
                                                    cell
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                </tbody>
            </table>
        </div>
    );
}
